import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../../lib/utils';

type ToastTone = 'success' | 'error' | 'warning' | 'info';

interface ToastState {
  id: number;
  message: string;
  tone: ToastTone;
  duration: number;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

const TOAST_DURATION: Record<ToastTone, number> = {
  success: 2_500,
  error: 5_000,
  warning: 4_500,
  info: 3_500,
};

const TOAST_META = {
  success: {
    icon: CheckCircle2,
    iconClassName: 'text-emerald-600',
    borderClassName: 'border-emerald-200',
  },
  error: {
    icon: AlertCircle,
    iconClassName: 'text-destructive',
    borderClassName: 'border-destructive/25',
  },
  warning: {
    icon: AlertTriangle,
    iconClassName: 'text-amber-600',
    borderClassName: 'border-amber-200',
  },
  info: {
    icon: Info,
    iconClassName: 'text-blue-600',
    borderClassName: 'border-blue-200',
  },
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const nextId = useRef(0);

  const show = useCallback((tone: ToastTone, message: string) => {
    const normalizedMessage = message.trim();
    if (!normalizedMessage) return;
    nextId.current += 1;
    setToast({
      id: nextId.current,
      message: normalizedMessage,
      tone,
      duration: TOAST_DURATION[tone],
    });
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: message => show('success', message),
      error: message => show('error', message),
      warning: message => show('warning', message),
      info: message => show('info', message),
    }),
    [show]
  );

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => {
      setToast(current => (current?.id === toast.id ? null : current));
    }, toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const meta = toast ? TOAST_META[toast.tone] : TOAST_META.info;
  const Icon = meta.icon;

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex justify-center px-4 sm:inset-x-auto sm:right-5 sm:justify-end sm:px-0">
        {toast && (
          <div
            key={toast.id}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
            className={cn(
              'admin-toast pointer-events-auto flex max-w-md items-center gap-2 rounded-lg border bg-background px-3 py-2.5 text-sm font-medium text-foreground shadow-[0_10px_30px_rgba(15,23,42,0.14)]',
              meta.borderClassName
            )}
            style={{ '--admin-toast-duration': `${toast.duration}ms` } as CSSProperties}
          >
            <Icon className={cn('h-4 w-4 flex-none', meta.iconClassName)} aria-hidden="true" />
            <span className="min-w-0 leading-5">{toast.message}</span>
            {toast.tone !== 'success' && (
              <button
                type="button"
                onClick={() => setToast(null)}
                className="ml-1 rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="关闭提示"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast 必须在 ToastProvider 内使用');
  return context;
}
